import axios from 'axios';
import { 
  AircraftContractor,
  ApiResponse,
  CreateContractorRequest,
  UpdateContractorRequest
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('teaam_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('teaam_token');
      localStorage.removeItem('teaam_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const contractorService = {
  // Get all contractors
  async getContractors(activeOnly: boolean = true): Promise<AircraftContractor[]> {
    try {
      const params = activeOnly ? '?active=true' : '';
      const response = await apiClient.get<ApiResponse<AircraftContractor[]>>(
        `/contractors${params}`
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch contractors');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch contractors');
    }
  },

  // Get single contractor
  async getContractor(id: string): Promise<AircraftContractor> {
    try {
      const response = await apiClient.get<ApiResponse<AircraftContractor>>(
        `/contractors/${id}`
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch contractor');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch contractor details');
    }
  },

  // Create contractor
  async createContractor(data: CreateContractorRequest): Promise<AircraftContractor> {
    try {
      const response = await apiClient.post<ApiResponse<AircraftContractor>>(
        '/contractors',
        data
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create contractor');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to create contractor');
    }
  },

  // Update contractor
  async updateContractor(id: string, data: UpdateContractorRequest): Promise<AircraftContractor> {
    try {
      const response = await apiClient.put<ApiResponse<AircraftContractor>>(
        `/contractors/${id}`,
        data
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update contractor');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update contractor');
    }
  },

  // Delete contractor (soft delete - set active to false)
  async deleteContractor(id: string): Promise<void> {
    try {
      await apiClient.put<ApiResponse<void>>(
        `/contractors/${id}`, 
        { active: false }
      );
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to delete contractor');
    }
  },

  // Get contractors for resources dropdown (includes phone info)
  async getContractorsForResources(): Promise<AircraftContractor[]> {
    try {
      const response = await apiClient.get<ApiResponse<AircraftContractor[]>>(
        '/contractors?includePhones=true'
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch contractors');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch contractors for resources');
    }
  },
};

export default contractorService;